import { isObjectEmpty } from "@omnia/shared/utils/objects"
import { hasOwnerLikeRole } from "../../../../shared/session/utils"
import { DefaultPermissions } from "../../defaults/defaults"
import { ApiInvite, ApiUser, HoldingPermissionsApi } from "./api"
import { GroupingKey } from "./components/cropping/types"
import { applyUpdatesToPermissions } from "./permissions"
import { croppingStateReducers } from "./state/cropping"
import { Cropping, EditorUser, HoldingRole, Invite, NewInvite, User, UserPermissions } from "./types"
import { cancelUserChanges, setupUserFromApi, validateUser } from "./users"

export interface State {

    api: HoldingPermissionsApi

    view:
    | "users"
    | "permissions"
    | "cropping"
    | "invites"

    loggedInUser: {

        /** User id */
        userId: number

        /**
         * If true, the current user has an owner-like role,
         * and can edit permissions, roles, and send invitations
         */
        ownerRole: boolean

        /**
         * Current permissions
         */
        permissions: UserPermissions

    }

    updated: boolean

    filters: {
        users: string
    }

    users: {
        users: User[]
        edits: EditorUser[]
    }

    invites: {

        /** All current invites */
        invites: Invite[],

        /** New invite */
        newInvite: NewInvite

    }

    cropping: {

        /** Grouping key */
        grouping: GroupingKey

        /** All cropping for the current year */
        cropping: Cropping[]

    }

    defaultPermissions: DefaultPermissions

}


export type View = State["view"]

type ActionType = Action["type"]

export type ActionPayload<ActionType extends Action["type"]> = Extract<Action, { type: ActionType }>["payload"]

export type Action =
    | {
        type: "load",
        payload: {
            users?: ApiUser[],
            invites?: ApiInvite[],
            defaultPermissions?: DefaultPermissions,
            cropping?: Cropping[]
        }
    }
    | {
        type: "setView",
        payload: View
    }
    | {
        type: "updateFilters",
        payload: Partial<State["filters"]>
    }
    | {
        type: "cancelChanges",
        payload?: never
    }
    | {
        type: "updateUser",
        payload: {
            userId: number,
            deleted?: boolean,
            role?: HoldingRole,
            restrictCroppingAccess?: boolean
        }
    }
    | {
        type: "updatePermissions",
        payload: {
            userId: number
            updates: {
                key: string
                values: {}
            }[]
        }
    }
    | {
        type: "updateNewInvite",
        payload: Partial<NewInvite>
    }
    | {
        type: "setCroppingGrouping",
        payload: GroupingKey
    }
    | {
        type: "setCroppingPermission",
        payload: {
            userId: number
            cropping: number[]
            authorized: boolean
        }
    }



const actions: { [type in ActionType]: (state: State, action: ActionPayload<type>) => State } = {

    load,

    setView,
    updateFilters,

    cancelChanges,

    updateUser,
    updatePermissions,

    ...croppingStateReducers,

    updateNewInvite

}


export function updateState<Type extends ActionType>(state: State, action: Extract<Action, { type: Type }>): State {

    const { type, payload } = action

    const reducer = actions[type]

    if (!reducer) {
        return state
    }

    state = reducer(state, payload as any)

    const updated = state.users.edits.some(u => u.updated || u.deleted)

    state = {
        ...state,
        updated
    }

    return state

}

export function getInitialState({ api, initialView }: { api: HoldingPermissionsApi, initialView?: View }): State {

    const ownerRole = hasOwnerLikeRole(api.session)

    const view = initialView ?? (
        ownerRole
            ? "users"
            : "permissions"
    )

    return {
        api,
        view,
        loggedInUser: {
            ...api.currentUser,
            ownerRole,
        },
        updated: false,
        filters: {
            users: undefined
        },
        users: {
            users: [],
            edits: []
        },
        invites: {
            invites: [],
            newInvite: {
                email: undefined,
                accessLevel: undefined
            }
        },
        cropping: {
            grouping: "crop",
            cropping: []
        },

        defaultPermissions: {} as DefaultPermissions

    }
}

function load(state: State, payload: ActionPayload<"load">): State {

    const defaultPermissions = payload.defaultPermissions ?? state.defaultPermissions

    const users = payload.users !== undefined
        ? payload.users.map(a => setupUserFromApi(a, state.loggedInUser.permissions, defaultPermissions)).sort((a, b) => a.displayName.localeCompare(b.displayName))
        : state.users.users

    const invites = payload.invites ?? state.invites.invites

    const cropping = payload.cropping ?? state.cropping.cropping

    const edits = users.map(u => {

        const restrictCroppingAccess = !isObjectEmpty(u.croppingPermissions)

        return {

            ...u,
            updated: false,
            deleted: false,
            valid: true,

            restrictCroppingAccess,

            initialState: {
                holdingRole: u.holdingRole,
                holdingPermissions: u.holdingPermissions,
                restrictCroppingAccess,
                croppingPermissions: u.croppingPermissions
            }

        }

    })


    return {
        ...state,
        defaultPermissions,
        users: {
            users,
            edits
        },
        invites: {
            ...state.invites,
            invites
        },
        cropping: {
            ...state.cropping,
            cropping
        }
    }

}

function cancelChanges(state: State): State {

    return updateUsers(state, cancelUserChanges)

}


function setView(state: State, view: ActionPayload<"setView">): State {
    return {
        ...state,
        view
    }
}

function updateFilters(state: State, payload: ActionPayload<"updateFilters">): State {
    return {
        ...state,
        filters: {
            ...state.filters,
            ...payload
        }
    }
}

function updateUser(state: State, { userId, role, deleted, restrictCroppingAccess }: ActionPayload<"updateUser">): State {

    return updateUsers(state, user => user.userId !== userId
        ? user
        : validateUser({
            ...user,
            holdingRole: role ?? user.holdingRole,
            deleted: deleted ?? user.deleted,
            restrictCroppingAccess: restrictCroppingAccess ?? user.restrictCroppingAccess
        }))

}

function updatePermissions(state: State, { userId, updates }: ActionPayload<"updatePermissions">): State {

    return updateUsers(state, user => user.userId !== userId
        ? user
        : validateUser({
            ...user,
            holdingPermissions: applyUpdatesToPermissions(user.holdingPermissions, updates)
        }))

}

function updateNewInvite(state: State, payload: ActionPayload<"updateNewInvite">): State {

    return {
        ...state,
        invites: {
            ...state.invites,
            newInvite: {
                ...state.invites.newInvite,
                ...payload
            }
        }
    }

}

export function updateUsers(state: State, map: (d: EditorUser) => EditorUser): State {

    const edits = state.users.edits.map(map)

    return {
        ...state,
        users: {
            ...state.users,
            edits
        }
    }
}
